/**
 * @fileoverview Cortex Conversation Storage Service - Phase 4 Compliant
 * @module services/cortex/conversation-storage
 * @description Handles persistence of voice conversations and transcriptions to Supabase.
 *
 * @implements {ICortexConversationStorage}
 * @implements {IService}
 * @service-registry
 * **Registry Configuration:**
 * - Key: 'cortexConversationStorage'
 * - Factory: `() => cortexConversationStorage`
 * - Dependencies: []
 * - Lifecycle: singleton
 *
 * @architecture Phase 4 compliant service
 * @version 1.0.0 - Phase 4 implementation
 * @since 1.0.0 - Modular architecture implementation
 */
import { typedSupabase } from '../../lib/typed-supabase';
import type { ICortexConversationStorage, VoiceConversation, VoiceMessage, } from './types';
import type { IService, ServiceResponse } from '../service-registry';
import { logger } from "../../lib/logger";
/**
 * Cortex Conversation Storage Service
 * Manages persistence of voice conversations and transcriptions
 */
class CortexConversationStorageService implements ICortexConversationStorage {
    readonly name = 'cortexConversationStorage' as const;
    private readonly STORAGE_KEY = 'cortex.conversations.cache';
    private readonly MAX_CACHE_SIZE = 50;
    /**
     * Initialize the service
     */
    async initialize(): Promise<void> {
        logger.info('🚀 Initializing Cortex Conversation Storage Service...');
        // Service is ready
    }
    /**
     * Dispose the service
     */
    async dispose(): Promise<void> {
        logger.info('🧹 Disposing Cortex Conversation Storage Service...');
        // Cleanup if needed
    }
    /**
     * Save a transcription message to the database
     */
    async saveTranscription(conversationId: string, message: VoiceMessage): Promise<ServiceResponse<void>> {
        try {
            // Save to Supabase
            const { error } = await typedSupabase
                .from('voice_messages')
                .insert({
                conversation_id: conversationId,
                role: message.role,
                transcription: message.transcription,
                audio_url: message.audio_url || null,
                timestamp: message.timestamp.toISOString(),
                duration: message.duration || null,
                is_final: message.is_final,
                streaming_segments: message.streaming_segments || null,
            });
            if (error) {
                logger.error('Failed to save transcription:', error);
                return { success: false, error: `Failed to save transcription: ${error.message}` };
            }
            // Update cache
            this.updateLocalCache(conversationId, message);
            return { success: true, data: undefined };
        }
        catch (error) {
            const errorMessage = error instanceof Error
                ? error.message
                : 'Failed to save transcription';
            return { success: false, error: errorMessage };
        }
    }
    /**
     * Get a conversation by ID
     */
    async getConversation(conversationId: string): Promise<ServiceResponse<VoiceConversation>> {
        try {
            // Try cache first
            const cached = this.getFromCache(conversationId);
            if (cached) {
                return { success: true, data: cached };
            }
            // Fetch from Supabase
            const { data, error } = await typedSupabase
                .from('voice_conversations')
                .select('*')
                .eq('id', conversationId)
                .single();
            if (error) {
                return { success: false, error: `Failed to get conversation: ${error.message}` };
            }
            if (!data) {
                return { success: false, error: 'Conversation not found' };
            }
            const conversation: VoiceConversation = {
                id: data.id,
                user_id: data.user_id,
                session_id: data.session_id,
                started_at: new Date(data.started_at),
                ended_at: data.ended_at ? new Date(data.ended_at) : undefined,
                total_duration: data.total_duration || undefined,
                message_count: data.message_count || 0,
                transcription_available: data.transcription_available || false,
                created_at: data.created_at ? new Date(data.created_at) : undefined,
                updated_at: data.updated_at ? new Date(data.updated_at) : undefined,
            };
            return { success: true, data: conversation };
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to get conversation';
            return { success: false, error: errorMessage };
        }
    }
    /**
     * List conversations for a user
     */
    async listConversations(userId: string, options?: {
        limit?: number;
        offset?: number;
    }): Promise<ServiceResponse<VoiceConversation[]>> {
        try {
            let query = typedSupabase
                .from('voice_conversations')
                .select('*')
                .eq('user_id', userId)
                .order('started_at', { ascending: false });
            if (options?.limit) {
                query = query.limit(options.limit);
            }
            if (options?.offset) {
                query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
            }
            const { data, error } = await query;
            if (error) {
                return { success: false, error: `Failed to list conversations: ${error.message}` };
            }
            const conversations: VoiceConversation[] = (data || []).map((row) => ({
                id: row.id,
                user_id: row.user_id,
                session_id: row.session_id,
                started_at: new Date(row.started_at),
                ended_at: row.ended_at ? new Date(row.ended_at) : undefined,
                total_duration: row.total_duration || undefined,
                message_count: row.message_count || 0,
                transcription_available: row.transcription_available || false,
                created_at: row.created_at ? new Date(row.created_at) : undefined,
                updated_at: row.updated_at ? new Date(row.updated_at) : undefined,
            }));
            return { success: true, data: conversations };
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to list conversations';
            return { success: false, error: errorMessage };
        }
    }
    /**
     * Delete a conversation
     */
    async deleteConversation(conversationId: string): Promise<ServiceResponse<void>> {
        try {
            // Delete messages first (cascade should handle this, but being explicit)
            await typedSupabase
                .from('voice_messages')
                .delete()
                .eq('conversation_id', conversationId);
            // Delete conversation
            const { error } = await typedSupabase
                .from('voice_conversations')
                .delete()
                .eq('id', conversationId);
            if (error) {
                return { success: false, error: `Failed to delete conversation: ${error.message}` };
            }
            // Remove from cache
            this.removeFromCache(conversationId);
            return { success: true, data: undefined };
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to delete conversation';
            return { success: false, error: errorMessage };
        }
    }
    /**
     * Update local cache
     */
    private updateLocalCache(conversationId: string, message: VoiceMessage): void {
        try {
            if (typeof window === 'undefined')
                return;
            const cached = localStorage.getItem(this.STORAGE_KEY);
            const conversations = cached ? JSON.parse(cached) : {};
            if (!conversations[conversationId]) {
                conversations[conversationId] = {
                    id: conversationId,
                    messages: [],
                };
            }
            conversations[conversationId].messages.push({
                ...message,
                timestamp: message.timestamp.toISOString(),
            });
            // Limit cache size
            const keys = Object.keys(conversations);
            if (keys.length > this.MAX_CACHE_SIZE) {
                const oldestKey = keys[0];
                delete conversations[oldestKey];
            }
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(conversations));
        }
        catch (error) {
            logger.warn('Failed to update local cache:', error);
        }
    }
    /**
     * Get from local cache
     */
    private getFromCache(conversationId: string): VoiceConversation | null {
        try {
            if (typeof window === 'undefined')
                return null;
            const cached = localStorage.getItem(this.STORAGE_KEY);
            if (!cached)
                return null;
            const conversations = JSON.parse(cached);
            return conversations[conversationId] || null;
        }
        catch (error) {
            logger.warn('Failed to read from cache:', error);
            return null;
        }
    }
    /**
     * Remove from local cache
     */
    private removeFromCache(conversationId: string): void {
        try {
            if (typeof window === 'undefined')
                return;
            const cached = localStorage.getItem(this.STORAGE_KEY);
            if (!cached)
                return;
            const conversations = JSON.parse(cached);
            delete conversations[conversationId];
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(conversations));
        }
        catch (error) {
            logger.warn('Failed to remove from cache:', error);
        }
    }
}
// Export singleton instance
export const cortexConversationStorage = new CortexConversationStorageService();
